import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @iconify
import { Icon } from '@iconify/react';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, useTheme, Switch } from '@mui/material';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import DashboardIcon from '@mui/icons-material/Dashboard';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useSettings from '../../../hooks/useSettings';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
// utils
import { userRedirectService, getDefaultRouteFromUserType } from '../../../utils/userRedirectService';

// ----------------------------------------------------------------------

// const MENU_OPTIONS = [
//   // {
//   //   label: 'Home',
//   //   linkTo: '/',
//   // },
//   // {
//   //   label: 'Profile',
//   //   linkTo: PATH_DASHBOARD.user.profile,
//   // },
//   {
//     label: 'Settings',
//     linkTo: PATH_DASHBOARD.profile.root,
//   },
// ];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { logout, user, refreshAuth } = useAuth();

  const userType = JSON.parse(localStorage.getItem('userType'));

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);

  const { onToggleMode } = useSettings();

  const theme = useTheme();

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true }); // Redirect to landing page instead of login

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error('Logout failed');
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const [isDashboardV2, setIsDashboardV2] = useState(localStorage.getItem('dashboardVersion') === 'v2');
  const [isRedirectMode, setIsRedirectMode] = useState(userRedirectService.isInRedirectMode());
  const [redirectedUserInfo, setRedirectedUserInfo] = useState(userRedirectService.getRedirectedUserInfo());
  const handleDashboardVersionChange = (event) => {
    const isV2 = event.target.checked;
    setIsDashboardV2(isV2);
    localStorage.setItem('dashboardVersion', isV2 ? 'v2' : 'v1');

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('dashboardVersionChanged'));

    // Optionally refresh the page or update the dashboard view
    window.location.reload();
  };

  const handleGoBackToParent = async () => {
    try {
      // Show loading state
      enqueueSnackbar('Switching back to parent user...', { variant: 'info' });

      // Switch back to parent (updates localStorage)
      userRedirectService.switchBackToParent();

      // Redirect to the restored parent user's default route
      const parentUserType = localStorage.getItem('userType');
      const redirectPath = getDefaultRouteFromUserType(parentUserType);

      setTimeout(() => {
        window.location.href = redirectPath;
      }, 100);

    } catch (error) {
      console.error('Switch back failed');
      enqueueSnackbar('Failed to switch back to parent user', { variant: 'error' });
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="h6" noWrap sx={{ textTransform: 'uppercase' }}>
            {user?.username}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {userType?.isSuperAdmin && 'SuperAdmin'}
            {userType?.isAdmin && 'Admin'}
            {userType?.isBroker && 'Broker'}
            {userType?.isClient && 'Client'}
          </Typography>

          {/* Redirect Mode Indicator */}
          {isRedirectMode && redirectedUserInfo && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, display: 'block' }}>
                🔄 REDIRECTED MODE
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                Logged in as: {redirectedUserInfo.username?.toUpperCase()}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* Theme Switch */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Light</Typography>
            <Switch checked={theme.palette.mode === 'dark'} onChange={onToggleMode} size="small" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Dark</Typography>
          </Box>
        </Stack>

        {/* Dashboard Version Switch */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewWeekIcon sx={{ fontSize: '1.1rem', color: isDashboardV2 ? 'text.secondary' : 'primary.main' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>v1</Typography>
            <Switch
              checked={isDashboardV2}
              onChange={handleDashboardVersionChange}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'primary.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>v2</Typography>
            <DashboardIcon sx={{ fontSize: '1.1rem', color: isDashboardV2 ? 'primary.main' : 'text.secondary' }} />
          </Box>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* Go Back Button - Only show in redirect mode */}
        {isRedirectMode && (
          <MenuItem
            onClick={handleGoBackToParent}
            sx={{
              m: 1,
              mb: 2,
              backgroundColor: alpha(theme.palette.info.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.info.main, 0.15),
              }
            }}
          >
            <Box
              component={Icon}
              icon="eva:arrow-back-outline"
              sx={{
                mr: 2,
                width: 20,
                height: 20,
                color: 'info.main',
              }}
            />
            <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 600 }}>
              ← Go Back to Parent
            </Typography>
          </MenuItem>
        )}

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </MenuPopover>
    </>
  );
}
